/**
 * useStatementReview.js
 * Review-state controller for the settle tab (statement status `review`, reopened included) — screen spec 2-4,
 * request contract 4-2·4-3. It used to live inside StatementReviewForm.vue; the form went away when item amounts moved
 * into the statement table ('고치기' per item) and the confirm controls moved into the aside card. SettleTab owns one
 * instance and hands it to StatementPanel (warning list), StatementSheet (inline amounts) and StatementConfirmCard
 * (checkboxes, memo, save, recalculate, confirm).
 *
 * Behavior kept from the form (unchanged contract):
 * - Six items (STATEMENT_LINE_KEYS). Each starts at the snapshot amount (`statement.lines.*.amount`, estimate =
 *   `statement.lines.*.estimate`, falling back to `charge.lines.*.amount`; the counselor-number item reads 0 when absent).
 *   An amount equal to its estimate is sent as `amount: null` ("follow the estimate"). Old keys (ai·counselor) are never sent.
 * - Amounts are integers 0 ~ STATEMENT_AMOUNT_MAX; no silent clamping — save/confirm warn with message.warning.
 * - Re-fetched details refill only untouched items (so a one-off write does not wipe typed amounts). After save /
 *   recalculate every item is refilled (markSynced).
 * - v70-legacy statements (`statement.legacy`): save/confirm are blocked (409 STATEMENT_LEGACY_LINES); recalculate stays.
 * - Blocking-warning acknowledgement: all blocking warnings must be checked. A warning whose content changed after a
 *   reload (count / details signature) loses its check; a status change clears all checks.
 * - The confirm modal keeps the request-doc sentence (supply amount) and adds one line with 청구 합계(부가세 포함) — the
 *   amount on the card's button — so one flow does not show two unexplained amounts (confirmWithVatText); the success
 *   toast says both. expected_total stays the supply amount (contract).
 * - Confirm: expected_rev always (statement.rev when nothing to save first, else the save response rev); expected_total only
 *   when the shown total equals `statement.total` and nothing is saved in the same step (expectedTotalFor). In a payment
 *   month (`payment_scope: in`) with unsaved input it asks twice (save → reload → confirm with the reloaded detail).
 *   When saving first changes a blocking warning (changedBlockingWarnings) it does not confirm, reloads, and points to the
 *   checklist. 409 STATEMENT_CONFLICT / STATEMENT_CHANGED / STATEMENT_LEGACY_LINES / STATEMENT_HAS_WARNINGS /
 *   PAYMENT_EXPECTED_REV_REQUIRED are explained then reloaded; "saved but not confirmed" is said when the save went through.
 * - Unsaved input (dirty) is mirrored into the shell's provide('settlementUnsavedInput') so leaving / changing month asks.
 * - Live margin uses the billed cost (settlementRules.billableCostOf): in a start month (cost.billable · cost.pre_start)
 *   it is cost.billable.total — the same formula as the server margin (charge − one-off − cost.billable), so the margin
 *   while typing equals the margin after saving. Other months read cost.total as before.
 * - 409 SETTLEMENT_NOT_STARTED (the month is before the billing start date — a stale screen): save / recalculate / confirm
 *   hand it to the shell's provide('settlementOnNotStarted') (notice + reload) and stop.
 *
 * Not a Vue component: call it from a component setup() (it uses inject / onBeforeUnmount).
 */
import { computed, h, inject, isRef, onBeforeUnmount, reactive, ref, watch } from 'vue';
import { Modal, message } from 'ant-design-vue';

import { formatWon, isNumber, monthLabel } from '../../service/settlementFormat';
import {
  SAVE_STATEMENT_CONFIRM_TEXT,
  STATEMENT_AMOUNT_MAX,
  STATEMENT_CONFLICT_TEXT,
  STATEMENT_LEGACY_LINES_TEXT,
  STATEMENT_LINE_KEYS,
  STATEMENT_LINE_LABELS,
  billableCostOf,
  blockingWarningSignature,
  changedBlockingWarnings,
  confirmStatementText,
  confirmWithVatText,
  isInternalDetail,
  isLegacyStatementDetail,
  marginOf,
  oneOffAmountOf,
  splitWarnings,
  statementLineRows,
  statementStatusOf,
  statementTotals,
  todoResolutionPlan,
  unacknowledgedBlocking,
  warningMeta,
} from '../../service/settlementRules';
import {
  PAYMENT_EXPECTED_REV_REQUIRED_TEXT,
  PAYMENT_SAVE_FIRST_TEXT,
  confirmBody,
  confirmFlowOf,
  confirmPaymentParagraph,
  expectedTotalFor,
  paymentScopeOf,
} from '../../service/settlementPayment';
import { isNotStartedError } from '../../service/settlementStart';
import { getCallApiErrorBody, getCallApiErrorCode, parseCallApiError } from '@/views/Callbot/service/callApiError';

/** Why save/confirm is off on a v70-legacy statement */
export const LEGACY_BLOCKED_TEXT = '요금표 v70 이전 정산서라 [다시 계산]으로 새 항목을 만든 뒤 저장·확정할 수 있습니다.';
/** Prefix when the confirm flow saved first and then could not confirm */
const SAVED_LEAD = '입력한 금액·메모는 저장했습니다. ';
/** n blocking warnings still unchecked — the confirm button's disabled reason, under the card's checklist legend */
const unacknowledgedText = (n) => `${n}건을 확인하면 켜집니다.`;
/** The same reason as a toast (no checklist next to it) — the panel calls them '확정 전에 볼 것' */
const unacknowledgedToastText = (n) => `확정 전에 볼 것 ${n}건을 확인해야 확정할 수 있습니다.`;

const arr = (v) => (Array.isArray(v) ? v : []);

/**
 * @param {{ companyId: string, month: string, detail: object, api: object, reload: Function }} props reactive props
 * @param {{ focusChecklist?: () => void }} [ui] UI hooks the controller calls after a flow ends (set by the owner)
 */
export function useStatementReview(props, ui = {}) {
  const status = computed(() => statementStatusOf(props.detail));
  const inReview = computed(() => status.value === 'review');
  /** Shell hook for 409 SETTLEMENT_NOT_STARTED — shows the notice and reloads (null outside the detail shell) */
  const onNotStarted = inject('settlementOnNotStarted', null);

  // -------------------------------------------------------------------------
  // Input state — form is what the operator typed, server is the last received value (the "touched?" baseline)
  // -------------------------------------------------------------------------

  const blankLine = () => ({ amount: null, memo: '' });
  const form = reactive(Object.fromEntries(STATEMENT_LINE_KEYS.map((key) => [key, blankLine()])));
  const server = reactive(
    Object.fromEntries(STATEMENT_LINE_KEYS.map((key) => [key, { amount: null, memo: '', estimate: null }]))
  );
  /** Save memo (goes to the close history) */
  const memo = ref('');
  /** 'save' | 'recalc' | 'confirm' | null */
  const busy = ref(null);
  /** Items whose inline amount editor is open ('고치기') */
  const openKeys = reactive(new Set());

  let initialized = false;
  // After recalculate / save every item is refilled from the next detail (input was sent or discarded)
  let resetOnSync = false;
  // Status of the detail the form was last filled from — entering review refills every item
  let syncedStatus = null;

  /** Fill the form from the (re)fetched detail — only untouched items take the new value */
  function sync() {
    const entering = inReview.value && syncedStatus !== 'review';
    for (const row of statementLineRows(props.detail)) {
      const f = form[row.key];
      const s = server[row.key];
      const untouched = f.amount === s.amount && f.memo === s.memo;
      if (!initialized || resetOnSync || entering || !inReview.value || untouched) {
        f.amount = row.amount;
        f.memo = row.memo;
      }
      Object.assign(s, { amount: row.amount, memo: row.memo, estimate: row.estimate });
    }
    if (!inReview.value) {
      memo.value = '';
      openKeys.clear();
    }
    initialized = true;
    resetOnSync = false;
    syncedStatus = status.value;
  }

  watch(() => props.detail, sync, { immediate: true });

  // -------------------------------------------------------------------------
  // Items and totals
  // -------------------------------------------------------------------------

  const warnings = computed(() => arr(props.detail.warnings));
  const split = computed(() => splitWarnings(warnings.value));
  const blocking = computed(() => split.value.blocking);
  const nonBlocking = computed(() => split.value.nonBlocking);
  const internal = computed(() => isInternalDetail(props.detail));
  const oneOff = computed(() => oneOffAmountOf(props.detail));
  /** v70-legacy statement — old item keys, save/confirm only after recalculate (409 STATEMENT_LEGACY_LINES) */
  const legacy = computed(() => isLegacyStatementDetail(props.detail));
  /** No default rate for the month (legacy included) — the server estimate (0) means "not computed" */
  const noRates = computed(() => !props.detail.rates || legacy.value);
  /** Amounts can be edited in the statement table */
  const editable = computed(() => inReview.value && !legacy.value);

  /** Per item: estimate, typed amount, edited (≠ estimate), unsaved (≠ last server value) */
  const items = computed(() =>
    Object.fromEntries(
      STATEMENT_LINE_KEYS.map((key) => {
        const f = form[key];
        const s = server[key];
        return [
          key,
          {
            key,
            estimate: s.estimate,
            amount: f.amount,
            memo: f.memo,
            edited: isNumber(s.estimate) && isNumber(f.amount) && f.amount !== s.estimate,
            unsaved: f.amount !== s.amount || f.memo !== s.memo,
            open: openKeys.has(key),
          },
        ];
      })
    )
  );

  const dirty = computed(
    () => inReview.value && (STATEMENT_LINE_KEYS.some((k) => items.value[k].unsaved) || memo.value.trim() !== '')
  );
  const anyEmpty = computed(() => STATEMENT_LINE_KEYS.some((k) => !isNumber(form[k].amount)));

  // Mirror unsaved input to the shell (SettlementDetail provide 'settlementUnsavedInput') so changing month or leaving
  // asks first. It stays set while the tab is inactive under KeepAlive; only unmount clears it
  const unsavedInput = inject('settlementUnsavedInput', null);
  if (isRef(unsavedInput)) {
    watch(
      dirty,
      (value) => {
        unsavedInput.value = value;
      },
      { immediate: true }
    );
  }

  const totals = computed(() => {
    if (anyEmpty.value) return null;
    const amounts = Object.fromEntries(STATEMENT_LINE_KEYS.map((key) => [key, form[key].amount]));
    return statementTotals({ ...amounts, oneOff: oneOff.value }, { internal: internal.value });
  });

  // Without a default rate there is no sale price to compare with the vox cost — margin is '—' (`margin: null`)
  const margin = computed(() =>
    totals.value && !noRates.value
      ? marginOf({
          total: totals.value.total,
          oneOff: oneOff.value,
          // Start month: cost.billable.total (server margin basis), else cost.total
          costTotal: billableCostOf(props.detail).total,
          internal: internal.value,
        })
      : null
  );

  function setAmount(key, value) {
    form[key].amount = value;
  }

  function setLineMemo(key, value) {
    form[key].memo = value;
  }

  function setMemo(value) {
    memo.value = value ?? '';
  }

  function resetLine(key) {
    if (isNumber(server[key].estimate)) form[key].amount = server[key].estimate;
  }

  function openLine(key) {
    if (editable.value && STATEMENT_LINE_KEYS.includes(key)) openKeys.add(key);
  }

  function closeLine(key) {
    openKeys.delete(key);
  }

  // -------------------------------------------------------------------------
  // Blocking-warning acknowledgement
  // -------------------------------------------------------------------------

  /** Checked blocking warning codes — the aside checkboxes and the warning list share it */
  const acknowledged = ref([]);
  const ackSet = computed(() => new Set(acknowledged.value));
  const unacknowledged = computed(() => unacknowledgedBlocking(warnings.value, acknowledged.value));

  /**
   * Warning content (count / details). A warning whose content changed after recalculate / save must be checked again
   * (saving re-judges the counselor-number duplicate suspicion). The confirm flow compares the same signature.
   */
  const blockingSignatures = computed(() =>
    Object.fromEntries(blocking.value.map((w) => [w.code, blockingWarningSignature(w)]))
  );

  // Drop checks of warnings that disappeared or changed (unchanged warnings keep their check across a save)
  watch(blockingSignatures, (now, before) => {
    const kept = acknowledged.value.filter((c) => c in now && (!before || now[c] === before[c]));
    if (kept.length !== acknowledged.value.length) acknowledged.value = kept;
  });

  // A status change (confirmed → reopened, …) asks for every check again
  watch(status, () => {
    acknowledged.value = [];
  });

  function toggleAck(code, checked) {
    const next = acknowledged.value.filter((c) => c !== code);
    if (checked) next.push(code);
    acknowledged.value = next;
  }

  function acknowledge(code) {
    if (!ackSet.value.has(code)) acknowledged.value = [...acknowledged.value, code];
  }

  const confirmBlockedReason = computed(() => {
    if (legacy.value) return LEGACY_BLOCKED_TEXT;
    if (anyEmpty.value) return '금액이 비어 있는 항목이 있습니다. 항목 금액을 넣어야 확정할 수 있습니다.';
    const n = unacknowledged.value.length;
    if (n) return unacknowledgedText(n);
    return '';
  });

  /** Toast text for the same block — it is read without the checklist next to it */
  const confirmBlockedToast = computed(() => {
    const n = unacknowledged.value.length;
    return !legacy.value && !anyEmpty.value && n ? unacknowledgedToastText(n) : confirmBlockedReason.value;
  });

  /**
   * The state panel already offers [다시 계산] up front — on a blocking warning's todo item, or on the v70-legacy line — so
   * the aside card does not repeat it
   */
  const recalcInTodo = computed(
    () =>
      inReview.value &&
      (legacy.value ||
        blocking.value.some((w) => todoResolutionPlan(w, 'review').primary.some((r) => r.action === 'recalculate')))
  );

  const saveBlockedReason = computed(() => {
    if (legacy.value) return LEGACY_BLOCKED_TEXT;
    return dirty.value ? '' : '바꾼 금액·메모가 없습니다';
  });

  // -------------------------------------------------------------------------
  // Writes — save · recalculate · confirm. Every write ends with reload()
  // -------------------------------------------------------------------------

  /** @returns {string|null} message.warning text */
  function lineError() {
    for (const key of STATEMENT_LINE_KEYS) {
      const v = form[key].amount;
      const label = STATEMENT_LINE_LABELS[key];
      if (v === null || v === undefined || v === '') return `${label} 금액을 넣어 주세요.`;
      const n = Number(v);
      if (!Number.isInteger(n) || n < 0) return `${label} 금액은 0 이상의 정수(원, 공급가액)로 넣어 주세요.`;
      if (n > STATEMENT_AMOUNT_MAX) return `${label} 금액은 ${formatWon(STATEMENT_AMOUNT_MAX)} 이하로 넣어 주세요.`;
    }
    return null;
  }

  /** Save body — an amount equal to its estimate goes as `amount: null` (follow the estimate) */
  function buildBody() {
    const lines = {};
    for (const key of STATEMENT_LINE_KEYS) {
      const amount = Number(form[key].amount);
      const estimate = server[key].estimate;
      lines[key] = {
        amount: isNumber(estimate) && amount === estimate ? null : amount,
        memo: (form[key].memo ?? '').trim(),
      };
    }
    return { lines, memo: memo.value.trim() };
  }

  /** Input was sent or discarded — refill every item from the next detail and clear the save memo */
  function markSynced() {
    resetOnSync = true;
    memo.value = '';
    openKeys.clear();
  }

  /** 409 SETTLEMENT_NOT_STARTED → the shell explains and reloads. true when handled */
  function handledNotStarted(err) {
    if (!isNotStartedError(err)) return false;
    if (typeof onNotStarted === 'function') onNotStarted();
    else props.reload();
    return true;
  }

  async function handleWriteError(err, fallback) {
    if (handledNotStarted(err)) return;
    const code = getCallApiErrorCode(err);
    if (code === 'STATEMENT_NOT_IN_REVIEW' || code === 'STATEMENT_NOT_FOUND') {
      message.warning('이 정산은 이제 검토 필요 상태가 아닙니다. 최신 상태로 다시 불러옵니다.');
      await props.reload();
      return;
    }
    if (code === 'STATEMENT_CONFLICT') {
      // Changed meanwhile by auto close / someone else — typed amounts survive the reload (sync)
      message.warning(STATEMENT_CONFLICT_TEXT);
      await props.reload();
      return;
    }
    if (code === 'STATEMENT_LEGACY_LINES') {
      message.warning(STATEMENT_LEGACY_LINES_TEXT);
      await props.reload();
      return;
    }
    message.error(parseCallApiError(err, fallback));
  }

  function onSave() {
    if (busy.value || saveBlockedReason.value) return;
    const error = lineError();
    if (error) {
      message.warning(error);
      return;
    }
    Modal.confirm({
      title: '정산 저장',
      content: SAVE_STATEMENT_CONFIRM_TEXT,
      okText: '저장',
      cancelText: '취소',
      // doSave never rejects — the OK button keeps loading until it ends
      onOk: () => doSave(),
    });
  }

  async function doSave() {
    busy.value = 'save';
    try {
      await props.api.saveStatement(props.companyId, props.month, buildBody());
      markSynced();
      message.success('정산을 저장했습니다. 검토 필요 상태 그대로이며, 자동 마감이 이 정산을 다시 계산하지 않습니다.');
      await props.reload();
    } catch (err) {
      await handleWriteError(err, '정산을 저장하지 못했습니다.');
    } finally {
      busy.value = null;
    }
  }

  function recalculate() {
    if (busy.value) return;
    if (!dirty.value) {
      doRecalculate();
      return;
    }
    Modal.confirm({
      title: '다시 계산',
      content: '저장하지 않은 입력이 있습니다. 다시 계산하면 입력한 금액·메모가 사라지고, 저장된 정산을 기준으로 다시 계산합니다.',
      okText: '다시 계산',
      cancelText: '취소',
      autoFocusButton: 'cancel',
      onOk: () => doRecalculate(),
    });
  }

  async function doRecalculate() {
    busy.value = 'recalc';
    try {
      await props.api.recalculateStatement(props.companyId, props.month);
      markSynced();
      message.success('지금 사용량·요율로 다시 계산했습니다. 사람이 넣은 항목 금액은 그대로 두었습니다.');
      await props.reload();
    } catch (err) {
      await handleWriteError(err, '다시 계산하지 못했습니다.');
    } finally {
      busy.value = null;
    }
  }

  /** Confirm modal body — one paragraph as plain text, several as <p> each. Empty (null) paragraphs are dropped */
  function confirmContent(paragraphs) {
    const list = paragraphs.filter(Boolean);
    return list.length > 1 ? h('div', list.map((text) => h('p', text))) : list[0];
  }

  function onConfirm() {
    if (busy.value || confirmBlockedReason.value) return;
    const error = lineError();
    if (error) {
      message.warning(error);
      return;
    }
    const scope = paymentScopeOf(props.detail);
    if (confirmFlowOf(scope, dirty.value) === 'two_step') {
      // Payment month with unsaved input — target/difference are computed from saved amounts, so ① save first and open
      // ② the confirm modal with the reloaded detail
      Modal.confirm({
        title: '이 달 확정',
        content: PAYMENT_SAVE_FIRST_TEXT,
        okText: '저장',
        cancelText: '취소',
        // saveThenConfirm never rejects — the [저장] button keeps loading until it ends
        onOk: () => saveThenConfirm(),
      });
      return;
    }
    const total = totals.value?.total ?? 0;
    const withVat = totals.value?.totalWithVat ?? null;
    const text = confirmStatementText(props.month, props.detail.company_name, total, scope);
    const paragraph = confirmPaymentParagraph(props.detail);
    const saveFirst = dirty.value;
    // Pin rev/total at open time. Saving first uses the save response rev (doConfirm) and sends no total
    const expectedRev = saveFirst ? undefined : props.detail.statement?.rev;
    const expectedTotal = expectedTotalFor({
      shownTotal: total,
      statementTotal: props.detail.statement?.total,
      savedInSameStep: saveFirst,
    });
    Modal.confirm({
      title: '이 달 확정',
      content: confirmContent([
        text,
        confirmWithVatText(withVat),
        paragraph,
        saveFirst ? '확정하기 전에 입력한 금액·메모를 먼저 저장합니다.' : null,
      ]),
      okText: '확정',
      cancelText: '취소',
      onOk: () => doConfirm(saveFirst, total, { expectedRev, expectedTotal, withVat }),
    });
  }

  // ② of the two-step flow opens only while the owner is mounted and the statement is still in review
  let unmounted = false;
  onBeforeUnmount(() => {
    unmounted = true;
    if (isRef(unsavedInput)) unsavedInput.value = false;
  });

  /**
   * Two-step flow ① [저장]: save → reload → open ② with the reloaded detail's text and payment paragraph. ② sends the save
   * response rev. If the reloaded detail cannot be confirmed now (the save raised / changed a blocking warning, …) it says
   * what was saved and why it stops, then points to the checklist. Never rejects.
   */
  async function saveThenConfirm() {
    busy.value = 'confirm';
    let res;
    try {
      res = await props.api.saveStatement(props.companyId, props.month, buildBody());
      markSynced();
      await props.reload();
    } catch (err) {
      await handleWriteError(err, '정산을 저장하지 못했습니다.');
      return;
    } finally {
      busy.value = null;
    }
    if (unmounted || !inReview.value) return;
    // Reload failed (the shell already said so) — the detail is still pre-save, so do not open ②
    const reloadedRev = props.detail.statement?.rev;
    const notReloaded = Number.isInteger(res?.rev) && Number.isInteger(reloadedRev) && reloadedRev < res.rev;
    if (notReloaded) return;
    if (confirmBlockedReason.value) {
      message.warning(`${SAVED_LEAD}${confirmBlockedToast.value}`);
      ui.focusChecklist?.();
      return;
    }
    if (lineError() !== null) return;
    const total = totals.value?.total ?? 0;
    const withVat = totals.value?.totalWithVat ?? null;
    const text = confirmStatementText(props.month, props.detail.company_name, total, paymentScopeOf(props.detail));
    const expectedTotal = expectedTotalFor({
      shownTotal: total,
      statementTotal: props.detail.statement?.total,
      savedInSameStep: false,
    });
    Modal.confirm({
      title: '이 달 확정',
      content: confirmContent([text, confirmWithVatText(withVat), confirmPaymentParagraph(props.detail)]),
      okText: '확정',
      cancelText: '취소',
      onOk: () => doConfirm(false, total, { saved: true, expectedRev: res?.rev, expectedTotal, withVat }),
    });
  }

  /** Remaining codes of 409 STATEMENT_HAS_WARNINGS */
  function remainingCodes(err) {
    const list = getCallApiErrorBody(err)?.error?.details?.warnings;
    return Array.isArray(list) ? list.map((w) => (typeof w === 'string' ? w : w?.code)).filter(Boolean) : [];
  }

  /**
   * Confirm (saving first when there is unsaved input). If saving first raised / changed a blocking warning it does not
   * confirm: reload and say so. Never rejects.
   * @param {boolean} saveFirst save here first
   * @param {number} total supply amount shown in the modal (success text)
   * @param {{ saved?: boolean, expectedRev?: number, expectedTotal?: number, withVat?: number|null }} [opts]
   *   withVat: 청구 합계(부가세 포함) shown in the modal — the success text repeats it
   */
  async function doConfirm(saveFirst, total, opts = {}) {
    busy.value = 'confirm';
    // If save went through and confirm fails, the save stays on the server — reload it (markSynced) so a retry does not
    // save the same amounts again
    let saved = !!opts.saved;
    let { expectedRev, expectedTotal } = opts;
    try {
      if (saveFirst) {
        // Warnings the operator saw (and checked) before saving
        const seen = warnings.value;
        const res = await props.api.saveStatement(props.companyId, props.month, buildBody());
        markSynced();
        saved = true;
        expectedRev = res?.rev;
        expectedTotal = undefined;
        const changed = changedBlockingWarnings(seen, res?.warnings);
        if (changed.length) {
          await props.reload();
          // The reloaded statement may have left review meanwhile (confirmed by someone else) — then the checklist hint
          // does not fit
          if (!unmounted && inReview.value) {
            message.warning(`${SAVED_LEAD}${confirmBlockedToast.value || unacknowledgedToastText(changed.length)}`);
            ui.focusChecklist?.();
          }
          return;
        }
      }
      const blockingCodes = new Set(blocking.value.map((w) => w.code));
      await props.api.confirmStatement(
        props.companyId,
        props.month,
        confirmBody({
          acknowledgedWarnings: acknowledged.value.filter((c) => blockingCodes.has(c)),
          expectedRev,
          expectedTotal,
        })
      );
      message.success(
        `${monthLabel(props.month)} 정산을 공급가액 ${formatWon(total)}(부가세 별도)으로 확정했습니다. ${confirmWithVatText(
          opts.withVat
        )}`.trim()
      );
      await props.reload();
    } catch (err) {
      if (handledNotStarted(err)) return;
      const savedLead = saved ? SAVED_LEAD : '';
      const code = getCallApiErrorCode(err);
      if (code === 'STATEMENT_CONFLICT' || code === 'STATEMENT_CHANGED') {
        message.warning(`${savedLead}${STATEMENT_CONFLICT_TEXT}`);
        await props.reload();
      } else if (code === 'STATEMENT_LEGACY_LINES') {
        message.warning(STATEMENT_LEGACY_LINES_TEXT);
        await props.reload();
      } else if (code === 'STATEMENT_HAS_WARNINGS') {
        const codes = remainingCodes(err);
        const list = codes.map((c) => `${warningMeta(c).label}(${c})`).join(', ');
        message.error(
          list
            ? `${savedLead}확인하지 않은 차단 경고가 남아 있습니다: ${list}. 경고를 확인한 뒤 다시 확정하세요.`
            : `${savedLead}확인하지 않은 차단 경고가 남아 있습니다. 경고를 확인한 뒤 다시 확정하세요.`
        );
        await props.reload();
      } else if (code === 'PAYMENT_EXPECTED_REV_REQUIRED') {
        message.warning(`${savedLead}${PAYMENT_EXPECTED_REV_REQUIRED_TEXT}`);
        await props.reload();
      } else if (saved) {
        const reason = parseCallApiError(err, '잠시 뒤 다시 확정하세요.');
        message.error(`입력한 금액·메모는 저장했지만 확정하지 못했습니다. ${reason}`);
        await props.reload();
      } else {
        await handleWriteError(err, '정산을 확정하지 못했습니다.');
      }
    } finally {
      busy.value = null;
    }
  }

  return {
    // state
    status,
    inReview,
    editable,
    form,
    memo,
    busy,
    acknowledged,
    ackSet,
    // derived
    items,
    dirty,
    anyEmpty,
    totals,
    margin,
    blocking,
    nonBlocking,
    legacy,
    noRates,
    internal,
    oneOff,
    unacknowledged,
    confirmBlockedReason,
    saveBlockedReason,
    recalcInTodo,
    // actions
    setAmount,
    setLineMemo,
    setMemo,
    resetLine,
    openLine,
    closeLine,
    toggleAck,
    acknowledge,
    onSave,
    recalculate,
    onConfirm,
  };
}
